import { useState } from "react";
import { api } from "../api.ts";
import { DEBT_ICONS, DebtDetailSheet, DebtsSummaryCard, NewDebtSheet, useDebts } from "../components/Debts.tsx";
import { EmptyState, SkeletonCard } from "../components/EmptyState.tsx";
import { PayoffPlanCard } from "../components/PayoffPlan.tsx";
import { currentMonth, dayTitle, money } from "../format.ts";
import { t, type Lang } from "../i18n.ts";
import { useRemote } from "../useRemote.ts";

/** Кредиты и рассрочки: сводка, список, добавление, подробности. */
export function DebtsPage({ lang, onBack }: { lang: Lang; onBack: () => void }) {
  const debts = useDebts();
  const month = currentMonth();
  // Сводка — из данных обзора (тот же кэш).
  const dash = useRemote(`dashboard:${month}`, () => api.dashboard(month));
  const [creating, setCreating] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);
  const list = debts.data ?? [];
  const active = list.filter((d) => d.status === "active");
  const closed = list.filter((d) => d.status === "closed");

  return (
    <main className="dash">
      <div className="list-header">
        <button className="icon-btn back-btn" onClick={onBack} aria-label="back">
          ←
        </button>
        <h2 className="page-heading">🏦 {t("debtsTitle")}</h2>
      </div>

      {dash.data?.debts && <DebtsSummaryCard s={dash.data.debts} lang={lang} />}
      {!debts.data && debts.loading && <SkeletonCard lines={3} />}
      {debts.data && list.length === 0 && (
        <EmptyState icon="🏦" title={t("noDebts")} text={t("noDebtsHint")} action={{ label: `＋ ${t("addDebt")}`, onClick: () => setCreating(true) }} />
      )}

      {[...active, ...closed].map((d) => {
        const pct = d.principal > 0 ? Math.min(100, Math.round(((d.principal - d.remaining) / d.principal) * 100)) : 0;
        return (
          <button key={d.id} type="button" className={d.status === "closed" ? "dash-card debt-card closed" : "dash-card debt-card"} onClick={() => setPicked(d.id)}>
            <div className="goal-line">
              <span className="goal-name">
                {DEBT_ICONS[d.kind]} {d.name}
                {d.personal && " 🔒"}
              </span>
              <b>{d.status === "closed" ? "✅" : money(d.remaining, d.currency, lang)}</b>
            </div>
            <span className="progress goal-progress">
              <span style={{ width: `${pct}%` }} />
            </span>
            <div className="goal-meta muted">
              {d.status === "closed"
                ? t("debtClosed")
                : d.nextPayment
                  ? `${dayTitle(d.nextPayment.dueDate, lang)} — ${money(d.nextPayment.amount, d.currency, lang)}${d.nextPayment.overdue ? ` · ⚠️ ${t("overdueL")}` : ""}`
                  : ""}
            </div>
          </button>
        );
      })}

      {active.length > 0 && <PayoffPlanCard debts={active} usdRate={dash.data?.usdRate ?? null} lang={lang} />}

      {list.length > 0 && (
        <button className="fab" onClick={() => setCreating(true)}>
          ＋ {t("addDebt")}
        </button>
      )}
      {creating && <NewDebtSheet lang={lang} onClose={() => setCreating(false)} />}
      {picked && <DebtDetailSheet id={picked} lang={lang} onClose={() => setPicked(null)} />}
    </main>
  );
}
